/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { StateObservable, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, map, switchMap } from 'rxjs/operators';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import { RootState, pollingTimer } from '../index';
import { Action, ActionCreators } from './actions';
import { ServiceTierRequestStatus } from '../../shared/entities/serviceTierRequest';
import ServiceTierRequest from '../../shared/entities/serviceTierRequest';

const requestServiceTierRequestsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedServiceTierRequestsSuccess.create(response);
};
const requestServiceTierRequestsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedServiceTierRequestsError.create(
    'Unable to fetch service tier requests. Please refresh page.',
  );
};

const updateServiceTierRequestSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.UpdateServiceTierRequestSuccess.create(response);
};
const updateServiceTierRequestError = (error: AjaxError): any => {
  return ActionCreators.UpdateServiceTierRequestError.create(
    `An error occurred while approving/denying service tier request. Please try again.`,
  );
};
const requestAvailableServiceTiersSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedAvailableServiceTiersSuccess.create(response);
};

const requestAvailableServiceTiersError = (error: AjaxError): any => {
  return ActionCreators.ReceivedAvailableServiceTiersError.create(error.xhr.response);
};

export const getServiceTierRequestsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestServiceTierRequests.type),
    pollingTimer(
      action$,
      state$.value.config.appConfig.apiPollingIntervalSeconds,
      ActionCreators.CancelServiceTierRequestsPoll.type,
    ),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get('/provisioning/service-tier-change-requests').pipe(
          map((response: AjaxResponse<any>): Action => requestServiceTierRequestsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestServiceTierRequestsError(error))
              ),
          ),
        ),
    ),
  );

const requestCurrentServiceTierChangeRequestSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCurrentServiceTierChangeRequestSuccess.create(response);
};

const requestCurrentServiceTierChangeRequestError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCurrentServiceTierChangeRequestError.create(error.xhr.response);
};

export const getCurrentServiceTierChangeRequestEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCurrentServiceTierChangeRequest.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/service-tier-change-requests/terminal/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => requestCurrentServiceTierChangeRequestSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestCurrentServiceTierChangeRequestError(error))
              ),
          ),
        ),
    ),
  );

const requestPostServiceTierRequestSuccess = (response: AjaxResponse<ServiceTierRequest>): any => {
  return ActionCreators.ReceivedPostServiceTierRequestSuccess.create(response.response);
};

const requestPostServiceTierRequestError = (error: AjaxError): any => {
  return ActionCreators.ReceivedPostServiceTierRequestError.create(error.xhr.response);
};

export const postServiceTierRequestEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.PostServiceTierRequest.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/provisioning/service-tier-change-requests', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => requestPostServiceTierRequestSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestPostServiceTierRequestError(error))
              ),
          ),
        ),
    ),
  );

export const getAvailableServiceTiersEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestAvailableServiceTiers.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get('/provisioning/service-tier-change-requests/options/available-service-tiers').pipe(
          map((response: AjaxResponse<any>): Action => requestAvailableServiceTiersSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestAvailableServiceTiersError(error))
              ),
          ),
        ),
    ),
  );

const requestDeleteServiceTierChangeRequestSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedDeleteServiceTierChangeRequestSuccess.create(response);
};

const requestDeleteServiceTierChangeRequestError = (error: AjaxError): any => {
  return ActionCreators.ReceivedDeleteServiceTierChangeRequestError.create(error.xhr.response);
};

export const deleteServiceTierChangeRequestEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteServiceTierChangeRequest.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.delete(`/provisioning/service-tier-change-requests/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => requestDeleteServiceTierChangeRequestSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestDeleteServiceTierChangeRequestError(error))
              ),
          ),
        ),
    ),
  );
export const updateServiceTierRequestEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.UpdateServiceTierRequest.type),
    switchMap(($action: any): Observable<Action> => {
      const baseUrl = `/provisioning/service-tier-change-requests/${$action.payload.requestId}`;
      const url =
        $action.payload.status === ServiceTierRequestStatus.COMPLETED
          ? baseUrl.concat('/complete')
          : baseUrl.concat('/deny');
      return ajaxRaw.put(url).pipe(
        map((response: AjaxResponse<any>): Action => updateServiceTierRequestSuccess(response)),
        catchError(
          (error: AjaxError): Observable<Action> =>
            handleErrorInterceptor(
              error,
              of(updateServiceTierRequestError(error))
            ),
        ),
      );
    }),
  );
